import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IconClose from '@/assets/icons/icon-close.svg?react';
import IconCursorMagic from '@/assets/icons/icon-cursor-magic-selection-02.svg?react';
import IconDelete from '@/assets/icons/icon-delete.svg?react';
import IconLogoutCircle from '@/assets/icons/icon-logout-circle-02.svg?react';
import IconPlusSign from '@/assets/icons/icon-plus-sign.svg?react';
import IconTimeQuarter from '@/assets/icons/icon-time-quarter-pass-stroke-rounded.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import { SelectField } from '@/components/dropdowns/SelectField/SelectField';
import { SliderField } from '@/components/inspector-fields/SliderField/SliderField';
import { SelectItem } from '@/components/ui/select';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { useInspectorNode } from '@/hooks/useInspectorNode';
import { computeFloatingPosition } from '@/utils/computeFloatingPosition';
import type { PopupProps } from '@builder4/shared/components/Popup';
import { POPUP_NODE_NAME } from '@builder4/shared/utils/constants';
import { PopupClickTriggerPanel } from './PopupClickTriggerPanel';

const TRIGGER_TYPES = ['click', 'exit', 'delay'] as const;
type TriggerType = (typeof TRIGGER_TYPES)[number];

type TriggerMeta = {
  label: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
};

const TRIGGER_META: Record<TriggerType, TriggerMeta> = {
  click: {
    label: 'Click',
    description: 'Appears instantly on clicking an assigned element, such as a button.',
    icon: IconCursorMagic,
  },
  exit: {
    label: 'Exit Intent',
    description: 'Appears when the visitor is about to leave the page.',
    icon: IconLogoutCircle,
  },
  delay: {
    label: 'Time Delay',
    description: 'Appears after the selected delay on page load.',
    icon: IconTimeQuarter,
  },
};

type TriggerSettings = Record<string, Record<string, unknown>>;

type TriggerEditPanelProps = {
  popupId: string;
  type: TriggerType;
  triggers: TriggerSettings;
  floatingPosition: { top: number; left: number };
  onClose: () => void;
  onUpdateTriggerConfig: (type: TriggerType, key: string, value: number) => void;
  onChangeTriggerType: (oldType: TriggerType, newType: TriggerType) => void;
  onRemoveTrigger: (type: TriggerType) => void;
};

const TriggerEditPanel = ({
  popupId,
  type,
  triggers,
  floatingPosition,
  onClose,
  onUpdateTriggerConfig,
  onChangeTriggerType,
  onRemoveTrigger,
}: TriggerEditPanelProps) => {
  const config = (triggers[type] || {}) as Record<string, unknown>;
  const isClick = type === 'click';
  const availableTypes = TRIGGER_TYPES.filter((t) => t === type || !triggers[t]);

  const title = isClick ? 'Click Trigger' : 'Custom Trigger';
  const sliderKey = type === 'exit' ? 'pageTime' : 'delay';
  const sliderLabel = type === 'exit' ? 'Page time' : 'Delay';
  const sliderTooltip =
    type === 'exit'
      ? 'Show the popup only when the visitor stayed on page for the selected time.'
      : '';

  return (
    <div
      className="bg-neutral-0 fixed z-50 w-[240px] rounded-lg shadow-[0px_12px_24px_0px_rgba(0,0,0,0.12)] outline outline-neutral-100"
      style={{ top: floatingPosition.top, left: floatingPosition.left }}
    >
      <div className="inline-flex h-10 w-full items-center justify-between rounded-t-lg border border-neutral-100 px-3">
        <div className="text-xs font-semibold text-neutral-900">{title}</div>
        <IconSimpleButton aria-label="Close" icon={IconClose} onClick={onClose} />
      </div>
      <div className="flex flex-col gap-3 border border-t-0 border-neutral-100 px-3 pt-3 pb-4">
        {isClick ? (
          <PopupClickTriggerPanel popupId={popupId} description={TRIGGER_META.click.description} />
        ) : (
          <>
            <SelectField
              key={type}
              text="Show when"
              size="small"
              defaultValue={type}
              onValueChange={(val) => {
                const newType = val as TriggerType;
                if (newType !== type) {
                  onChangeTriggerType(type, newType);
                }
              }}
            >
              {availableTypes.map((t) => (
                <SelectItem key={t} value={t}>
                  {TRIGGER_META[t].label}
                </SelectItem>
              ))}
            </SelectField>
            <p className="text-xs text-neutral-500">{TRIGGER_META[type].description}</p>
            <SliderField
              label={sliderLabel}
              tooltip={sliderTooltip}
              value={(config[sliderKey] as number) || 0}
              onChange={(v) => onUpdateTriggerConfig(type, sliderKey, v)}
              min={0}
              max={60}
              suffix="s"
            />
            <hr className="border-neutral-100" />
            <SecondaryButton
              className="w-full"
              size="sm"
              text="Remove Trigger"
              icon={IconDelete}
              onClick={() => onRemoveTrigger(type)}
            />
          </>
        )}
      </div>
    </div>
  );
};

export const InspectorPopupTrigger = () => {
  const { id, props: nodeProps, node, actions } = useInspectorNode();
  const { setBPProp, getBPProp } = useBreakpointAwareProp(
    node?.data.displayName || POPUP_NODE_NAME,
  );

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const [editingType, setEditingType] = useState<TriggerType | null>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const triggers = (getBPProp(nodeProps, 'triggers') as TriggerSettings | undefined) || {
    click: {},
  };
  const triggerKeys = TRIGGER_TYPES.filter((t) => t in triggers);

  const handleAddTrigger = () => {
    const nextType = TRIGGER_TYPES.find((t) => !triggers[t]);
    if (!nextType) return;

    const updated = { ...triggers, [nextType]: {} };
    actions.setProp(id, (props: PopupProps) => {
      setBPProp(props, 'triggers', updated);
    });
  };

  const handleEdit = (type: TriggerType) => {
    if (editingType === type) {
      setEditingType(null);
      setPos(null);
      return;
    }

    const inspectorEl = anchorRef.current?.closest('.inspector') as HTMLElement | null;
    const p = inspectorEl
      ? computeFloatingPosition({
          inspectorEl,
          anchorEl: anchorRef.current,
          width: 240,
          height: 360,
          preferSide: 'left',
        })
      : null;
    if (p) {
      setPos({ top: p.top, left: p.left });
      setEditingType(type);
    }
  };

  const handleUpdateTriggerConfig = (type: TriggerType, key: string, value: number) => {
    const updated = {
      ...triggers,
      [type]: { ...triggers[type], [key]: value },
    };
    actions.setProp(id, (props: PopupProps) => {
      setBPProp(props, 'triggers', updated);
    });
  };

  const handleChangeTriggerType = (oldType: TriggerType, newType: TriggerType) => {
    const updated = Object.fromEntries(Object.entries(triggers).filter(([key]) => key !== oldType));
    updated[newType] = {};
    actions.setProp(id, (props: PopupProps) => {
      setBPProp(props, 'triggers', updated);
    });
    setEditingType(newType);
  };

  const handleRemoveTrigger = (type: TriggerType) => {
    const updated = Object.fromEntries(Object.entries(triggers).filter(([key]) => key !== type));
    actions.setProp(id, (props: PopupProps) => {
      setBPProp(props, 'triggers', updated);
    });
    setEditingType(null);
    setPos(null);
  };

  const allAdded = TRIGGER_TYPES.every((t) => triggers[t]);

  return (
    <div ref={anchorRef} className="flex max-w-[216px] flex-col gap-2">
      <p className="pb-1 text-xs font-semibold text-neutral-900">Triggers</p>

      {triggerKeys.map((type) => {
        const meta = TRIGGER_META[type];
        if (!meta) return null;
        const Icon = meta.icon;
        return (
          <div key={type} className="flex items-center gap-2">
            <div className="flex h-8 flex-1 items-center gap-2 rounded-md bg-neutral-50 px-2">
              <Icon className="h-4 w-4 flex-shrink-0 text-neutral-500" />
              <span className="text-button-sm truncate font-medium text-neutral-900">
                {meta.label}
              </span>
            </div>
            <SecondaryButton size="sm" text="Edit" icon={null} onClick={() => handleEdit(type)} />
          </div>
        );
      })}

      {!allAdded && (
        <div className="flex items-center justify-between">
          <span className="text-button-sm font-medium text-neutral-500">New Trigger</span>
          <SecondaryButton size="sm" icon={IconPlusSign} text="Add" onClick={handleAddTrigger} />
        </div>
      )}

      {editingType &&
        pos &&
        createPortal(
          <TriggerEditPanel
            popupId={id}
            type={editingType}
            triggers={triggers}
            floatingPosition={pos}
            onClose={() => {
              setEditingType(null);
              setPos(null);
            }}
            onUpdateTriggerConfig={handleUpdateTriggerConfig}
            onChangeTriggerType={handleChangeTriggerType}
            onRemoveTrigger={handleRemoveTrigger}
          />,
          document.body,
        )}
    </div>
  );
};
